import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, ShoppingBag, UserPlus, Music2, Star, FileSignature, CheckCheck, Mail, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { NotifReply } from "./NotifReply";

const KIND_META = {
  sale: { icon: ShoppingBag, label: "Vente" },
  artist: { icon: UserPlus, label: "Artiste" },
  client: { icon: UserPlus, label: "Client" },
  track: { icon: Music2, label: "Morceau" },
  review: { icon: Star, label: "Avis" },
  contract: { icon: FileSignature, label: "Contrat" },
  contact: { icon: Mail, label: "Contact" },
  general: { icon: Bell, label: "Info" },
};

const relTime = (iso) => {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

export const AdminNotifications = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = useCallback(() => {
    api.get("/admin/notifications")
      .then((r) => {
        setItems(r.data.notifications || []);
        setUnread(r.data.unread || 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markAllRead = async () => {
    try {
      await api.post("/admin/notifications/read");
      setUnread(0);
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
    } catch {
      /* silencieux */
    }
  };

  const removeNotif = async (n) => {
    try {
      await api.delete(`/admin/notifications/${n.notif_id}`);
      setItems((arr) => arr.filter((x) => x.notif_id !== n.notif_id));
      if (!n.read) setUnread((u) => Math.max(0, u - 1));
    } catch {
      /* silencieux */
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="admin-notif-bell"
        title="Notifications"
        className="relative flex h-11 w-11 items-center justify-center border border-white/15 text-zinc-400 transition-colors hover:border-amber-500/60 hover:text-amber-400"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span
            data-testid="admin-notif-badge"
            className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-black"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="admin-notif-panel"
          className="absolute right-0 z-50 mt-3 w-96 max-w-[90vw] border border-white/10 bg-[#0c0c0e] shadow-2xl shadow-black/60"
        >
          <div className="flex items-center justify-between border-b border-white/[0.08] px-5 py-4">
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              Notifications {unread > 0 && <span className="text-amber-500">({unread} non lue{unread > 1 ? "s" : ""})</span>}
            </p>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                data-testid="admin-notif-mark-read"
                className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-amber-400 transition-colors hover:text-amber-300"
              >
                <CheckCheck className="h-3.5 w-3.5" /> Tout marquer lu
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && (
              <p className="px-5 py-8 text-center text-sm text-zinc-600" data-testid="admin-notif-empty">
                Aucune notification pour le moment.
              </p>
            )}
            {items.slice(0, 20).map((n) => {
              const meta = KIND_META[n.kind] || KIND_META.general;
              const Icon = meta.icon;
              return (
                <div
                  key={n.notif_id}
                  data-testid={`admin-notif-item-${n.notif_id}`}
                  className={`flex gap-3 border-b border-white/[0.05] px-5 py-4 ${n.read ? "opacity-60" : ""}`}
                >
                  <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center border ${n.read ? "border-white/10 text-zinc-500" : "border-amber-500/40 bg-amber-500/10 text-amber-400"}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="truncate text-sm font-medium text-zinc-100">{n.title}</p>
                      <span className="flex shrink-0 items-center gap-2 text-[10px] text-zinc-600">
                        {relTime(n.created_at)}
                        <button
                          onClick={() => removeNotif(n)}
                          data-testid={`admin-notif-delete-${n.notif_id}`}
                          title="Supprimer cette notification"
                          className="text-zinc-600 transition-colors hover:text-red-400"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    </div>
                    <p className="mt-0.5 text-[10px] uppercase tracking-[0.2em] text-zinc-600">{meta.label}</p>
                    {(n.lines || []).map((l, i) => (
                      <p key={i} className="mt-1 text-xs leading-relaxed text-zinc-400">{l}</p>
                    ))}
                    <NotifReply notif={n} />
                  </div>
                </div>
              );
            })}
          </div>
          <Link
            to="/studio/notifications"
            onClick={() => setOpen(false)}
            data-testid="admin-notif-view-all"
            className="block border-t border-white/[0.08] px-5 py-3.5 text-center text-[10px] font-bold uppercase tracking-[0.25em] text-amber-400 transition-colors hover:bg-amber-500/10"
          >
            Tout voir — page Notifications
          </Link>
        </div>
      )}
    </div>
  );
};
